import React, {useContext, useState} from 'react';
import {Box, Card, Grid, IconButton} from '@mui/material';
import {ArrowDownward} from '@mui/icons-material';
import constants from "../../../../../../config/constants";
import {GameStateContext} from "../GeneratedHome";

export const BoardComponent = ({gameId, board, players, actions}) => {

    // Use GameStateContext to access the cardSelectionActions and setState
    const { state, setState } = useContext(GameStateContext);

    const [ selectedCaseFromId, setSelectedCaseFromId ] = useState(null);

    // Base URL for the card images
    const baseImageUrl = constants.api.basePath + "/generated/" + gameId + "/image";

    const hasChoice = (caseId) => {
        return actions && (
            (!selectedCaseFromId && actions.some(action => action.caseFromId === caseId || action.caseId === caseId)) ||
            (selectedCaseFromId && actions.some(action => action.caseFromId === selectedCaseFromId && action.caseToId === caseId))
        );
    };

    const handleCardClick = (caseId) => {
        if(selectedCaseFromId && selectedCaseFromId === caseId) {
            setSelectedCaseFromId(null);
            return;
        }
        if(!hasChoice(caseId)) {
            setSelectedCaseFromId(null);
            return;
        }
        let choice;
        if(selectedCaseFromId) {
            choice = actions.find(action => action.caseFromId === selectedCaseFromId && action.caseToId === caseId);
        } else {
            if(actions.some(action => action.caseFromId === caseId)){
                setSelectedCaseFromId(caseId);
                return;
            }
            choice = actions.find(action => action.caseId === caseId);
        }
        if (choice) {
            setState(prevState => ({
                ...prevState,
                action: choice,
                show: true
            }));
        }
    };

    const selectColChoices = actions && actions.filter(action => action.type === "SelectColumnChoice");

    const handleColumnSelect = (col) => {
        const choice = selectColChoices.find(action => action.col === col);
        if (choice) {
            setState(prevState => ({
                ...prevState,
                action: choice,
                show: true
            }));
        }
    };

    const getPieceColor = (piece) => {
        for(let player of Object.values(players)){
            if(player.playerId === piece.playerId){
                return player.color;
            }
        }
        return "";
    }

    return (
        <Box
            className="board-game"
            sx={{
                width: { xs: "80%", md: "50%" },
                margin: "0 auto",
                padding: 2
            }}
        >
            {selectColChoices && selectColChoices.length > 0 && (
                <Grid container spacing={1} sx={{ marginBottom: 1 }}>
                    {Array.from({ length: board.cols }, (_, col) => {
                        const hasColChoice = selectColChoices.some(action => action.col === col);
                        return (
                            <Grid
                                item
                                xs={12 / board.cols}
                                key={`col-${col}`}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <IconButton
                                    onClick={hasColChoice ? () => handleColumnSelect(col) : undefined}
                                    disabled={!hasColChoice}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '1/1',
                                        backgroundColor: hasColChoice ? '#2a2d4a' : '#15161f',
                                        border: '2px solid #1f2136',
                                        borderRadius: 1,
                                        cursor: hasColChoice ? 'pointer' : 'default',
                                        '&:hover': hasColChoice ? {
                                            backgroundColor: '#1f70ea',
                                            borderColor: '#10346f'
                                        } : {},
                                        '&:disabled': {
                                            opacity: 0.3
                                        }
                                    }}
                                >
                                    <ArrowDownward
                                        sx={{
                                            color: hasColChoice ? '#ffffff' : '#666666',
                                            fontSize: '1.5rem'
                                        }}
                                    />
                                </IconButton>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1/1',
                    border: '2px solid #1f2136',
                    borderRadius: 1,
                    backgroundColor: '#0a0a0a'
                }}
            >
                {board.cases && board.cases.map((caseItem, index) => {
                    // Normalize coordinates from 0-1024 to 0-100%
                    const leftPercent = (caseItem.centerX / 1024) * 100;
                    // Flip Y coordinate since CSS uses top-left origin, data uses bottom-left
                    const topPercent = (1 - (caseItem.centerY / 1024)) * 100;
                    const sizePercent = (caseItem.size / 1024) * 100;

                    const isClickable = hasChoice(caseItem.id) || (selectedCaseFromId && selectedCaseFromId === caseItem.id);

                    return (
                        <Card
                            key={caseItem.id || index}
                            onClick={() => handleCardClick(caseItem.id)}
                            sx={{
                                position: 'absolute',
                                left: `${leftPercent - (sizePercent / 2)}%`,
                                top: `${topPercent - (sizePercent / 2)}%`,
                                width: `${sizePercent}%`,
                                height: `${sizePercent}%`,
                                backgroundColor: isClickable ? '#2a2d4a' : (selectedCaseFromId && selectedCaseFromId === caseItem.id) ? '#10346f' : '#15161f',
                                border: '2px solid #1f2136',
                                borderRadius: 1,
                                cursor: isClickable ? 'pointer' : 'default',
                                opacity: 1,
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': isClickable ? {
                                    backgroundColor: '#1f70ea',
                                    borderColor: '#10346f'
                                } : {}
                            }}
                        >
                            {board.pieces && board.pieces.filter(piece => piece.caseId === caseItem.id).map((piece, pieceIndex) => (
                                <img
                                    key={pieceIndex}
                                    style={{width: "100%", height: "auto"}}
                                    src={`${baseImageUrl}?imagePath=piece_${piece.name}${piece.playerId ? '_' + getPieceColor(piece): ''}.png`}
                                    alt={piece.name}
                                />
                            ))}
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
}
